// Angular imports
import { Component, OnInit, OnDestroy, ViewChild, ViewChildren, ElementRef, HostListener, QueryList, Input, NgZone } from '@angular/core';

// Import rxjs
import { BehaviorSubject, Observable, Subject, animationFrameScheduler, auditTime, combineLatest, debounceTime, delayWhen, distinctUntilChanged, filter, finalize, fromEvent, interval, map, merge, pairwise, switchMap, takeUntil, takeWhile, tap, throttleTime, timer, withLatestFrom } from 'rxjs';

// Import types
import { CalculationLineApi, CalculationMetaApi, CalculationTailApi } from '../../types/calculation';

// Import column and selection types
import { Rect } from '../types';

// Import components
import { LineRowComponent } from '../line-row/line-row.component';

// Import constants
import { Trie } from 'src/app/types/trie';
import { Store } from '@ngrx/store';
import { CalculationSelection, ColumnDefinitions, ViewModelLineType, ViewModelLine, isCalculationLineApi, isCalculationTailApi } from 'src/app/state-management/calculation-state';
import { partsShown, selectCalculationMeta, selectColumnDefinitions, selectCurrentSelection, selectLines, selectMaxVersion, selectTableLayout, selectViewlines } from 'src/app/state-management/calculation-selectors';
import * as CalculationActions from 'src/app/state-management/calculation-actions';
import { AppState } from 'src/app/state-management/app-state';
import { TextLineElementDirective } from 'src/app/directives/text-line-element.directive';
import { TailRowComponent } from '../tail-table/line-row/line-row.component';
import { CreateLineService } from 'src/app/services/create-line/create-line.service';

// Constants
const DEFAULT_LINE_HEIGHT = 20;
const DEFAULT_TOTAL_LINE_HEIGHT = 20;
const MIN_HANDLE_HEIGHT = 20; // Minimum height of the scrollbar handle in pixels


// New approach with Redux state management
declare type ViewModelDisplayLine = ViewModelLine & {
    estimatedHeight?: number;
    actualHeight?: number;
    yPos?: number;
    warnings?: any;
    index?: number;
    selected?: boolean;
}

// ==================================================

@Component({
    selector: 'paged-line-table',
    templateUrl: './paged-line-table.component.html',
    styleUrls: ['./paged-line-table.component.scss'],
    standalone: false
})
export class PagedLineTableComponent implements OnInit, OnDestroy {
    /**
     * Used to remove all subscribers on destroy
     *
     * @private
     * @memberof PagedLineTableComponent
     */
    private unsubscribe$ = new Subject<void>();

    // New approach with Redux state management
    @ViewChildren(TextLineElementDirective) lineElements: QueryList<TextLineElementDirective>;

    // Main viewport with three layers
    @ViewChild('virtualScrollContainer') virtualScrollContainer!: ElementRef<HTMLDivElement>;
    // Scrollbar container
    @ViewChild('scrollbarContainer') scrollbarContainer!: ElementRef<HTMLDivElement>;
    // Scrollbar background
    @ViewChild('scrollbarBackground') scrollbarBackground!: ElementRef<HTMLCanvasElement>;
    // Scrollbar handle
    @ViewChild('scrollHandle') scrollHandle!: ElementRef<HTMLDivElement>;
    // Scrollbar handle
    @ViewChild('position') position!: ElementRef<HTMLDivElement>;
    // Need the line row still to set focus to cells et cetera
    @ViewChildren(LineRowComponent) public _lineRowComponents: QueryList<LineRowComponent>;
    @ViewChildren(TailRowComponent) public _tailRowComponents: QueryList<TailRowComponent>;

    @HostListener('window:resize', ['$event'])
    async onResize() {
        // console.log('RESIZE');

        this.adjustCanvasSize();
    }

    public allowedUnits: string[] = [];

    // Whether or not the current version is locked
    public _isLocked: boolean = false;

    // Selection
    selection$ = new BehaviorSubject<CalculationSelection | undefined>(undefined);
    _selection$ = this.selection$.asObservable();

    meta$ = new BehaviorSubject<CalculationMetaApi>(undefined);
    _percentages$ = this.meta$.pipe(
        takeUntil(this.unsubscribe$),
        // tap(meta => console.log('meta', meta.percentages)),
        map(meta => meta?.percentages)
    )

    public _selectionCurrentHighlight?: Rect;
    public _selectionOriginHighlight?: Rect;
    public _selectionHighlight?: Rect;

    // Scrolling
    scrollHandleHeight: number = 30; // It will be recalculated based on content size
    scrollHandlePosition: number = 0;
    scrollHandleMovement$: Observable<number>;

    // Measured text lines are stored to a Trie structure
    public textLineHeights = new Trie<number>();

    // When the scrollable contents is less high than the scroll window, hide the scroll handle
    hideScrollHandle$ = new BehaviorSubject<boolean>(false);

    // All lines in the application state, converted to view model lines
    // With extra properties for displaying in the table
    lines$ = this.store.select(selectViewlines);

    // Lines currently visible
    visibleLines$ = new BehaviorSubject<ViewModelDisplayLine[]>([]);

    displayVisibleLines$ = this.visibleLines$.pipe(
        // debounceTime(50)
    );

    partsShown$ = new BehaviorSubject<string>('all');

    /**
     * Adjustment for the viewport position, since this logic only renders lines as a whole, starting from the top of the viewport.
     * So the DOM element is moved to mimic scrolling distances less than the height of a line
     *
     * @memberof PagedLineTableComponent
     */
    scrollOffset = new BehaviorSubject<number>(0);

    /**
     * The actual scroll position of the viewport. This is used to calculate the position of the scrollbar handle and the visible lines.
     *
     * @memberof PagedLineTableComponent
     */
    scrollTop$ = new BehaviorSubject<number>(0);

    /**
     * The total height of all visible lines. This is used to calculate the position of the scrollbar handle and the visible lines.
     *
     * @memberof PagedLineTableComponent
     */
    scrollableContentHeight$ = new BehaviorSubject<number>(1000);

    /**
     * During scroll animation, the target scroll position is stored here. This is used to calculate the speed of the scroll animation.
     *
     * @private
     * @memberof PagedLineTableComponent
     */
    private scrollTarget = this.scrollTop$.value

    /**
     * Flag to indicate whether or not the component is currently animating. This is used to prevent multiple animations from running at the same time.
     *
     * @private
     * @memberof PagedLineTableComponent
     */
    private isAnimating = false

    /**
     * The height of the viewport. This is the height of the rendered lines
     *
     * @memberof PagedLineTableComponent
     */
    viewPortHeight$ = new BehaviorSubject<number>(1000);

    /**
     * All lines in the current calculation. These are the view model lines, which are used to display the lines in the table.
     *
     * @memberof PagedLineTableComponent
     */
    allLines$ = new BehaviorSubject<ViewModelDisplayLine[]>([]);

    calculationLines$ = new BehaviorSubject<CalculationLineApi[]>([]);

    constructor(
        private store: Store<AppState>,
        private ngZone: NgZone,
        private createLineService: CreateLineService) {
        // this.scrollTop$.next(0);
        // this.scrollableContentHeight$.next(1000);
        // this.viewPortHeight$.next(1000);
    }

    public ngOnDestroy() {
        // New unsubscribe strategy
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public ngOnInit() {
        // this.scrollTop$.subscribe((value) => {
        //     console.log('SCROLL TOP', value);
        // })

        this.store.select(selectLines).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(lines => this.calculationLines$.next(lines));

        this.store.select(partsShown).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(parts => {
            this.partsShown$.next(parts);
        })

        combineLatest([this.lines$, this.partsShown$]).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(([lines, parts]) => {
            // console.log('COMBINED', { lines, parts });
            const mappedLines = lines.map(line => this.transformLineToViewModel(line))
                .filter(f => {
                    //   console.log('Filtering:', { parts, section: f.section });
                    return parts === 'all' || parts === f.section;
                });

            // console.log('MAPPED', mappedLines);

            this.allLines$.next(mappedLines);

            // Initiate size calculations
            setTimeout(() => this.measureLineHeights(true));
        });

        // Fill visible lines
        combineLatest([
            this.scrollTop$.pipe(throttleTime(50), distinctUntilChanged()),
            this.allLines$
        ]).pipe(
            takeUntil(this.unsubscribe$),
        ).subscribe(() => {
            this.updateVisibleLines();
        })

        // Update selection rects
        combineLatest([
            this.scrollTop$,
            // .pipe(auditTime(100)),
            this.allLines$,
            this.store.select(selectCurrentSelection)
        ]).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(() => {
            // Update rects in next frame
            this.updateSelectionRects();
        })

        this.store.select(selectCurrentSelection).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe((selection) => {
            // console.log(selection);
            this.selection$.next(selection);

            // Check scroll position
            const rowIndex = selection?.current.row;

            if (!rowIndex) {
                return;
            }

            const selectionYPos = [...this.allLines$.value].slice(0, rowIndex).reduce((p, c) => p + (c.actualHeight ?? c.estimatedHeight ?? 0), 0);
            if (selectionYPos < (this.scrollTop$.value + this.scrollOffset.value) || selectionYPos > (this.viewPortHeight$.value + this.scrollTop$.value + this.scrollOffset.value)) {
                // Selection is out of view
                this.handleScrollTarget(selectionYPos);
            }
        })

        this.store.select(selectCalculationMeta).pipe(
            takeUntil(this.unsubscribe$),
            map(meta => !!meta?.includeProvisionalPostLinesAfterTail),
            distinctUntilChanged()
        ).subscribe(include => {
            this.allowedUnits = [
                ...['doz', 'dgn', 'dzd', 'kg', 'km', 'krt', 'ltr', 'm1', 'm2', 'm3', 'm', 'pak', '%', 'pst', 'set', 'spt', 'stk', 'uur', 'wkn'], // These units are always allowed
                ...(include ? ['sptN'] : [])  // sptN is only allowed if the includeProvisional... prop is true
            ]
        });

        combineLatest([
            this.store.select(selectCalculationMeta),
            this.store.select(selectMaxVersion)
        ]).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(([meta, maxVersion]) => {
            this._isLocked = (meta?.currentVersion ?? 0) !== (maxVersion ?? -1);
        })

        this.store.select(selectCalculationMeta)
            .pipe(
                takeUntil(this.unsubscribe$)
            ).subscribe(meta => {
                this.meta$.next(meta);
            });

        this.store.select(selectColumnDefinitions)
            .pipe(
                takeUntil(this.unsubscribe$)
            ).subscribe(defs => this._columns = defs);

        this.handleColumnChange();
    }

    private handleColumnChange() {
        this.store.select(selectColumnDefinitions).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(defs => {
            // console.log('COLUMN CHANGE', defs);
            this._columns = defs;
        });
    }

    private viewInitialized: boolean = false;

    ngAfterViewInit() {
        this.viewInitialized = true;
        // Set up subscriptions for virtual scroll function
        // this.adjustCanvasSize();
        this.setUpScrolling();
    }

    ngAfterViewChecked() {
        if (this.viewInitialized) {

            this.viewInitialized = false;
        }
    }

    private setUpScrollingOutsideZoneJs() {
        this.ngZone.runOutsideAngular(() => {
            // flag to track dragging state
            let isDragging = false

            // 1) wheel events
            const wheel$ = fromEvent<WheelEvent>(
                this.virtualScrollContainer.nativeElement,
                'wheel',
                { passive: true }
            ).pipe(
                takeUntil(this.unsubscribe$),
                throttleTime(50),
                tap(e => e.stopPropagation()),         // prevent page scroll
                map(e => Math.max(-200, Math.min(e.deltaY, 200)))
            )
            const wheelIntent$ = wheel$.pipe(
                map(delta => this.limitScrollPosition(this.scrollTarget + delta))
            )

            // 2) drag scrollbar handle
            const mouseMove$ = fromEvent<MouseEvent>(document, 'mousemove')
            const mouseUp$ = fromEvent<MouseEvent>(document, 'mouseup')
            const handleDown$ = fromEvent<MouseEvent>(
                this.scrollHandle.nativeElement,
                'mousedown'
            )

            const scrollHandleMovement$ = handleDown$.pipe(
                // reset dragging flag when a new drag starts
                tap(() => { isDragging = false }),
                switchMap(startE => {
                    const startY = startE.clientY
                    const startPos = this.scrollHandlePosition ?? 0
                    let moved = false

                    return mouseMove$.pipe(
                        tap(moveE => {
                            moveE.stopPropagation()
                            moved = true                         // mark that user is dragging
                            isDragging = true
                        }),
                        map(moveE => moveE.clientY - startY + startPos),
                        takeUntil(mouseUp$.pipe(
                            tap(() => {
                                // reset flag right after drag ends
                                setTimeout(() => { isDragging = false }, 0)
                            })
                        )),
                        distinctUntilChanged()
                    )
                })
            )

            // 3) clicking outside the handle
            const clickScrollbar$ = fromEvent<MouseEvent>(
                this.scrollbarContainer.nativeElement,
                'click'
            ).pipe(
                tap(e => e.stopPropagation()),
                // only process real clicks, not end-of-drag
                filter(() => !isDragging),
                map(e => e.offsetY - this.scrollHandleHeight / 2)
            )

            // 4) map drag & click to absolute targets
            const dragIntent$ = merge(
                scrollHandleMovement$,
                clickScrollbar$
            ).pipe(
                map(newHandlePos => {
                    const contMin = this.scrollableContentHeight$.value - this.viewPortHeight$.value
                    const maxY = this.viewPortHeight$.value - this.scrollHandleHeight
                    const pos = Math.max(0, Math.min(newHandlePos, maxY))
                    return pos * (contMin / maxY)
                })
            )

            // 5) merge all intents, filter duplicates en dispatch
            const scrollIntent$ = merge(
                wheelIntent$,
                dragIntent$
            ).pipe(
                distinctUntilChanged(),
                takeUntil(this.unsubscribe$),
                tap(() => {
                    document.body.classList.add('no-select')
                    this.store.dispatch(CalculationActions.startScrolling())
                })
            )

            scrollIntent$.subscribe(target => {
                this.handleScrollTarget(target)
            })

            // reset no-select na mouseup
            mouseUp$.subscribe(() => {
                document.body.classList.remove('no-select')
            })
        })
    }

    private setUpScrolling() {
        this.setUpScrollingOutsideZoneJs();

        combineLatest([
            this.scrollTop$,
            // .pipe(auditTime(100)),
            this.viewPortHeight$,
            this.scrollableContentHeight$
        ]).pipe(
            distinctUntilChanged(),
            takeUntil(this.unsubscribe$),
            map(([scrollY, viewPort, content]) => {
                const maxHandlePosition = viewPort - this.scrollHandleHeight;

                // Scale scroll position back to handle position
                const handlePosition = (scrollY / ((content - viewPort) || 1)) * maxHandlePosition;

                return handlePosition;
            })
        ).subscribe((pos) => {
            this.scrollHandlePosition = pos ?? 0;
            // Update your handle's style to reflect the new position
            this.scrollHandle.nativeElement.style.top = `${pos}px`;
        })

        combineLatest([this.viewPortHeight$, this.scrollableContentHeight$]).pipe(
            takeUntil(this.unsubscribe$),
            // distinctUntilChanged((prev, curr) => prev[0] === curr[0] && prev[1] === curr[1])
        ).subscribe(([viewPort, content]) => {
            // console.log('RESIZE HANDLE', viewPort, content);
            this.scrollHandleHeight = viewPort > 0 && content > 0 ? Math.max(Math.pow(viewPort, 2) / content, MIN_HANDLE_HEIGHT) : MIN_HANDLE_HEIGHT;

            if (this.scrollHandle && this.scrollHandle.nativeElement) {
                this.scrollHandle.nativeElement.style.height = `${this.scrollHandleHeight}px`;
            }

            // Hide the scrollbar if content fits in container
            const noSrollbarNeeded = content < viewPort;

            setTimeout(() => {
                this.hideScrollHandle$.next(noSrollbarNeeded);
            }, 0)

            // Adjust scroll top on resizing, if it exceeds maximum value
            let scrollTopShouldBe = noSrollbarNeeded ? 0 : Math.min(this.scrollTop$.value, this.scrollableContentHeight$.value - this.viewPortHeight$.value);

            if (scrollTopShouldBe !== this.scrollTop$.value) {
                this.scrollTop$.next(scrollTopShouldBe);
            }

            // Draw scrollbar background
            this.drawScrollbarBackground();
        })

        this.adjustCanvasSize();
    }

    // // When scrolling, do not pass the page limits
    // private trySetScrollPosition(pos: number) {
    //     const limitedPos = this.limitScrollPosition(pos);
    //     this.scrollTop$.next(limitedPos);
    // }

    private limitScrollPosition(pos: number) {
        this.store.dispatch(CalculationActions.startScrolling());
        return Math.max(0, Math.min(pos, this.scrollableContentHeight$.value - this.viewPortHeight$.value));
    }

    animatedScroll(scrollToValue: number) {
        this.ngZone.runOutsideAngular(() => {
            const start = performance.now();
            const from = this.scrollTop$.value;
            const to = this.limitScrollPosition(scrollToValue);
            const delta = to - from;
            const dist = Math.abs(delta);
            // calculate duration based on distance, clamped
            const speed = 0.5;      // pixels per ms
            const minDuration = 200;      // ms
            const maxDuration = 1000;     // ms
            const duration = Math.min(
                Math.max(dist / speed, minDuration),
                maxDuration
            );

            // easeInOutQuad function: slow-fast-slow
            const easeInOutQuad = (t: number) =>
                t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

            const step = (now: number) => {
                const elapsed = now - start;
                const t = Math.min(elapsed / duration, 1);
                const eased = easeInOutQuad(t);
                const current = from + delta * eased;

                // only update inside Angular when needed
                this.ngZone.run(() => {
                    this.scrollTop$.next(current);
                });

                this.measureLineHeights(true);

                if (t < 1) {
                    requestAnimationFrame(step);
                }
            };

            requestAnimationFrame(step);
        });
    }

    private handleScrollTarget(targetValue: number) {
        // clamp and set the new scroll target
        const clamped = this.limitScrollPosition(targetValue)
        this.scrollTarget = clamped
        // kick off animation loop if not already running
        this.startAnimationLoop()
    }

    private startAnimationLoop() {
        if (this.isAnimating) return
        this.isAnimating = true

        const easeFactor = 0.2  // adjust for stiffness
        const step = () => {
            const from = this.scrollTop$.value
            const diff = this.scrollTarget - from

            if (Math.abs(diff) < 0.5) {
                // close enough, snap to target and stop
                this.ngZone.run(() => this.scrollTop$.next(this.scrollTarget))
                this.isAnimating = false
                return
            }

            // simple ease-in-out like behavior via factor
            const move = diff * easeFactor

            this.ngZone.run(() => this.scrollTop$.next(from + move))
            this.measureLineHeights(true)

            requestAnimationFrame(step)
        }

        requestAnimationFrame(step)
    }

    private updateVisibleLines(): void {
        const t0 = new Date().valueOf();

        const firstSelected = this.selection$.value?.selectedLines.find(Boolean);
        const visibleLines: ViewModelDisplayLine[] = [];
        const viewportHeight = window.innerHeight;
        let currentHeight = 0;
        let firstVisibleHeight = 0;
        // console.log('UPDATE VISIBLE LINES', this.allLines$.value.length);


        for (let i = 0; i < (this.allLines$.value?.length ?? 0); i++) {
            // Skip vat lines, they are not displayed in the table
            if (this.allLines$.value[i].hidden) {
                continue;
            }

            const line = this.allLines$.value[i];

            const lineHeight = line.actualHeight ?? line.estimatedHeight;

            if ((currentHeight + lineHeight) > this.scrollTop$.value && currentHeight < (this.scrollTop$.value + viewportHeight + firstVisibleHeight)) {
                firstVisibleHeight = firstVisibleHeight || lineHeight;
                line.yPos = currentHeight;
                line.selected = line.line?.id && firstSelected?.id == line.line?.id;
                visibleLines.push(line);
            }

            currentHeight += lineHeight;

            if (currentHeight >= this.scrollTop$.value + viewportHeight) {
                break;
            }
        }

        this.scrollOffset.next((visibleLines[0]?.yPos ?? 0) - this.scrollTop$.value);
        this.visibleLines$.next(visibleLines);
        this.viewInitialized = true;

        // console.log('VISIBLE LINES', visibleLines[0]?.yPos, this.scrollTop$.value,  this.scrollOffset.value);
        // console.log(visibleLines)

        // console.log('updateVisibleLines', new Date().valueOf() - t0);
        // console.log('updateVisibleLines', visibleLines);
    }

    private measureLineHeights(forceRecalculate: boolean = false): void {
        const t0 = new Date().valueOf();

        let delta = 0;
        let updateNeeded = false;

        this.lineElements?.forEach((lineElementDirective: TextLineElementDirective) => {
            // Access the component
            const component = lineElementDirective.lineRowComponent;

            // Check if it is a text line
            // if ((lineElementDirective.lineRowComponent.line as CalculationLineApi).isTextLine) {
            // If so, measure the element
            // Get the id from the component
            const id = component.line.id;
            // Look for this id in the visible lines
            const viewLine = this.visibleLines$.value.find(f => f.line?.id === id);
            // Measure height
            const element = lineElementDirective.elementRef.nativeElement;
            const height = element.offsetHeight;

            if (!!viewLine && viewLine.actualHeight !== height) {
                updateNeeded = true;

                delta += ((viewLine.actualHeight ?? 0) - height);

                viewLine.actualHeight = height;
                // Insert in Trie storage
                this.textLineHeights.insert(id, height);
            }
            // }
        });

        // console.log('UPDATE NEEDED?', updateNeeded);

        if (updateNeeded) {
            setTimeout(() => {
                this.updateVisibleLines();
            });
        }

        // console.log('FORCE RECALC?', !!delta || forceRecalculate);

        // Only recalculate if the total of changed values is not 0
        if (!!delta || forceRecalculate) {
            // console.log('RECALCULATE', delta, forceRecalculate);

            setTimeout(() => {
                this.recalculateContentHeight();
            });
        }

        // console.log('measureLineHeights', new Date().valueOf() - t0);
    }

    private recalculateContentHeight() {
        const newHeight = this.allLines$.value.reduce((p, c) => p + (c.hidden ? 0 : c.actualHeight ?? c.estimatedHeight ?? DEFAULT_LINE_HEIGHT), 0);

        // console.log('RECALCULATE HEIGHT', this.allLines$.value.map(m => m.actualHeight ?? m.estimatedHeight), newHeight);

        this.scrollableContentHeight$.next(newHeight);
    }

    public transformLineToViewModel(line: ViewModelLine): ViewModelDisplayLine {
        if (line.type === ViewModelLineType.TEXT || line.type === ViewModelLineType.TAIL_TEXT) {
            const savedHeight = this.textLineHeights.search(line.line.id);
            if (savedHeight) {
                return {
                    ...line,
                    estimatedHeight: savedHeight,
                    actualHeight: savedHeight
                }
            } else {
                const estimatedHeight = this.estimateHeight(line);
                return {
                    ...line,
                    estimatedHeight
                }
            }
        } else {
            return {
                ...line,
                estimatedHeight: line.type === ViewModelLineType.TOTAL ? DEFAULT_TOTAL_LINE_HEIGHT : DEFAULT_LINE_HEIGHT,
                actualHeight: line.type === ViewModelLineType.TOTAL ? DEFAULT_TOTAL_LINE_HEIGHT : DEFAULT_LINE_HEIGHT,
                warnings: line.warnings ?? []
            }
        }
    }

    private estimateHeight(line: ViewModelLine): number {
        // Logic to estimate height based on line type and content
        const { line: { description = '' } = { description: '' } } = line;
        return (((description ?? '').match(/(?:<br\s?\?>|<div>)/g) ?? []).length + 1) * DEFAULT_LINE_HEIGHT;
    }

    drawScrollbarBackground() {
        const t0 = new Date().valueOf();
        const ctx = this.scrollbarBackground?.nativeElement.getContext('2d');

        if (!ctx) {
            return;
        }

        // Clear everything
        ctx!.clearRect(0, 0, this.scrollbarContainer.nativeElement.offsetWidth, this.scrollbarContainer.nativeElement.offsetHeight);

        const viewPortContentRatio = this.viewPortHeight$.value / Math.max(this.viewPortHeight$.value, this.scrollableContentHeight$.value);

        let curY = 0;

        // console.log('DRAW SCROLLBAR', this.allLines$.value);

        this.allLines$.value.forEach((row) => {

            // Skip vat lines, they are not displayed in the table
            if (!row.hidden) {

                const rowHeight = row.type === ViewModelLineType.TEXT ? row.actualHeight ?? row.estimatedHeight ?? DEFAULT_LINE_HEIGHT : DEFAULT_LINE_HEIGHT;
                const lineWidth = rowHeight * viewPortContentRatio;
                ctx.lineWidth = lineWidth;

                switch (row.type) {
                    case ViewModelLineType.TEXT:
                        ctx.strokeStyle = '#00ff0033';
                        break;
                    case ViewModelLineType.CATEGORY:
                        ctx.strokeStyle = '#6872A6';
                        break;
                    case ViewModelLineType.SUBCATEGORY:
                        ctx.strokeStyle = '#9395BF';
                        break;
                    default:
                        ctx.strokeStyle = '#fff';
                        break;
                }

                // Draw line on canvas
                // Set grid line style
                ctx.beginPath();

                ctx.moveTo(0, curY * viewPortContentRatio + (lineWidth / 2));
                ctx.lineTo(30, curY * viewPortContentRatio + (lineWidth / 2));

                ctx.stroke();

                ctx.closePath();

                // Warnings
                if (row.warnings?.length > 0) {
                    ctx.lineWidth = Math.max(1, lineWidth - 4);
                    ctx.strokeStyle = '#ff0000';
                    // ctx.strokeStyle = '#C5A555';
                    ctx.beginPath();

                    ctx.moveTo(5, curY * viewPortContentRatio + (lineWidth / 2));
                    ctx.lineTo(19, curY * viewPortContentRatio + (lineWidth / 2));

                    ctx.stroke();

                    ctx.closePath();
                }

                curY += rowHeight;
            }
        })

        // console.log('DRAW SCROLLBAR', new Date().valueOf() - t0);
    }

    async adjustCanvasSize() {
        // The DIV element containing the canvas for the table body
        const bodyContainer = this.virtualScrollContainer.nativeElement;

        // Update viewport size
        this.viewPortHeight$.next(bodyContainer.offsetHeight);

        // Scrollbar container
        const scrollbarContainer = this.scrollbarContainer.nativeElement;
        // Scrollbar canvas
        const scrollbarCanvas = this.scrollbarBackground.nativeElement;

        // Fit the scrollbar canvas
        scrollbarCanvas.width = scrollbarContainer.offsetWidth;
        scrollbarCanvas.height = scrollbarContainer.offsetHeight;

        // Resizing will erase the drawing, so redraw
        this.drawScrollbarBackground();
    }

    // Handle selection
    private getCellBoundingRect(lineIndex: number, columnIndex: number): Rect {
        let rect: Rect;

        // // Attempt to get bounding rect through active components. This will return undefined if the line is not currently rendered
        rect = this._lineRowComponents.find(lineRow => lineRow.index === lineIndex)?.getCellRect(columnIndex);

        // Return rect if line was on screen
        if (rect) return rect;
    }

    public moveSelectionUp(setOrigin: boolean = false) {
        this.store.dispatch(CalculationActions.moveSelectionUp());
    }

    public moveSelectionDown(setOrigin: boolean = false) {
        this.store.dispatch(CalculationActions.moveSelectionDown());
    }

    public moveSelectionLeft(setOrigin: boolean = false) {
        this.store.dispatch(CalculationActions.moveSelectionLeft());
    }

    public moveSelectionRight(setOrigin: boolean = false) {
        this.store.dispatch(CalculationActions.moveSelectionRight());
    }

    public clearSelection(): void {
        // // Assign seletion
        // this._selection.origin = undefined;
        // this._selection.current = { row: null, column: null };

        // // Update application context
        // this.applicationContextService.selectedLine$ = null; // Current line
        // this.applicationContextService.set('selectedLines', this.selectedLines); // All lines

        // // Update selection rects
        // this.updateSelectionRects();
    }

    public mergeSelectionToOrigin(): void {
        // if (this._selection.origin) {
        //     // Set selection
        //     this._selection.current = this._selection.origin!;
        // }

        // // Ensure the selection is set to a valid line
        // if (!this.selectedLine) this.moveSelectionDown();
        // if (!this.selectedLine) this.moveSelectionUp();

        // // Update application context
        // this.applicationContextService.selectedLine$ = this.selectedLine; // Current line
        // this.applicationContextService.set('selectedLines', this.selectedLines); // All lines

        // // Update selection rects
        // this.updateSelectionRects();
    }

    public updateSelectionRects(): void {
        setTimeout(() => {
            // Get the bounding rect of the selection current if it exists
            const rect = !!this.selection$.value?.current?.row ? this.getCellBoundingRect(this.selection$.value.current.row, this.selection$.value.current.column) : undefined;

            // Get the bounding rect of the selection origin if it exists
            // const originRect = this._lineRowComponents.find(lineRow => lineRow.index === this._selection.origin?.row)?.selectionOriginCellRect;
            const originRect = this.selection$.value?.origin ? this.getCellBoundingRect(this.selection$.value.origin!.row, this.selection$.value.origin!.column) : undefined;

            // Set selection highlight
            this._selectionCurrentHighlight = rect ? { ...rect, width: rect.width - 1 } : undefined;
            this._selectionOriginHighlight = originRect ? { ...originRect, width: originRect.width - 1 } : undefined;

            this._selectionHighlight = rect && originRect ? {
                top: Math.min(rect.top, originRect.top),
                left: Math.min(rect.left, originRect.left),
                height: Math.max(rect.top + rect.height, originRect.top + originRect.height) - Math.min(rect.top, originRect.top),
                width: Math.max(rect.left + rect.width, originRect.left + originRect.width) - Math.min(rect.left, originRect.left)
            } : undefined;
        });
    }

    public focusSelectedCell(shallow: boolean = true) {
        // Find the line row that matches the selected index
        const lineRow = this._lineRowComponents.find(lineRow => lineRow.index === this.selection$.value?.current.row);

        // If no line row matched the currently selected row index, return
        if (!lineRow)
            return;

        if (isCalculationLineApi(lineRow.line)) {
            // Focus the selected column
            lineRow.focusCell(this.selection$.value.current.column, shallow && !lineRow.line.isTextLine);
        } else if (isCalculationTailApi(lineRow.line)) {
            lineRow.focusCell(this.selection$.value.current.column, shallow);
        }
    }

    public _handleLineChange() {
        // console.log('UPDATE', line);
        // if(isCalculationLineApi(line)){
        //     this.store.dispatch(CalculationActions.updateLine({ line, projectId: this.meta$.value?.projectId, calculationId: this.meta$.value?.id }));
        // } else if(isCalculationTailApi(line)){
        //     this.store.dispatch(CalculationActions.updateTailLine({ line, projectId: this.meta$.value?.projectId, calculationId: this.meta$.value?.id }));
        // }
    }

    // public scrollTo(scrollPosition: number, callback?: () => void): void {
    //     const translatedPos = Math.max(
    //         0,
    //         Math.min(
    //             scrollPosition,
    //             this.scrollableContentHeight$.value - this.viewPortHeight$.value
    //         )
    //     )

    //     if (this.scrollTop$.value === translatedPos) {
    //         return
    //     }

    //     this.animatedScroll(translatedPos);
    // }

    // =================================================================================

    @ViewChild('tableBody', { static: true }) public tableBody: ElementRef<HTMLDivElement>;

    // @ViewChild(TotalRowComponent) public _totalRowComponent: TotalRowComponent;
    // @ViewChildren(CategoryRowComponent) public _categoryRowComponents: QueryList<CategoryRowComponent>;
    // @ViewChildren(SubCategoryRowComponent) public _subcategoryRowComponents: QueryList<SubCategoryRowComponent>;
    // @ViewChildren(SubCategoryTotalRowComponent) public _subcategoryTotalRowComponents: QueryList<SubCategoryTotalRowComponent>;

    @Input() public active: boolean = false;

    @Input() public set config(value: 'default' | 'condensed') {
    }

    // Map holding column properties
    public _columns: ColumnDefinitions;

    // Paging margins
    public _topMargin: number = 0;
    public _bottomMargin: number = 0;

    /* --- Event handlers --- */

    public _handlePassOnScroll(event: WheelEvent) {
        if (document.activeElement && ['INPUT', 'DIV'].includes(document.activeElement.tagName)) {
            (document.activeElement as HTMLElement).blur();
        }

        this.tableBody.nativeElement.scrollTop += event.deltaY;
    }

    @HostListener('window:cut', ['$event'])
    public _handleCut(event: ClipboardEvent) {
        // Perform regular copy event
        this._handleCopy(event);

        // Remove line
        const removeCalculationLines = this.selection$.value.selectedLines?.filter(f => isCalculationLineApi(f)).map<CalculationLineApi>(l => l as CalculationLineApi);

        if (!!removeCalculationLines.length) {
            this.store.dispatch(CalculationActions.deleteLines({
                projectId: this.meta$.value.projectId,
                calculationId: this.meta$.value.id,
                lines: removeCalculationLines
            }))
        }

        // Remove tail line
        const removeTailLines = this.selection$.value.selectedLines?.filter(f => isCalculationTailApi(f)).map<CalculationTailApi>(l => l as CalculationTailApi);

        if (!!removeCalculationLines?.length) {
            this.store.dispatch(CalculationActions.deleteTailLines({
                projectId: this.meta$.value.projectId,
                calculationId: this.meta$.value.id,
                lines: removeTailLines
            }))
        }
    }

    @HostListener('window:copy', ['$event'])
    public _handleCopy(event: ClipboardEvent) {
        // console.log(this.selection$.value);
        // Get the current selected line
        const selected = this.selection$.value?.selectedLines.map(l => l);
        if (!selected) return;

        console.log('COPY', selected);

        // Assign clipboard
        event.clipboardData.setData('application/lines-data', JSON.stringify(selected));

        // Prevent default
        event.preventDefault();
    }

    @HostListener('window:paste', ['$event'])
    public async _handlePaste(event: ClipboardEvent) {
        // Get the current selected line
        const selected = this.selection$.value.selectedLines?.[0];
        if (!selected) return;

        // Get copy data
        const data = event.clipboardData.getData('application/lines-data') as string | undefined;
        if (!data) return;

        const parsedLines = JSON.parse(data) as CalculationLineApi[] | CalculationTailApi[];

        if (isCalculationLineApi(selected)) {
            // Check if data contains only calculation lines
            if (!parsedLines.every(isCalculationLineApi)) {
                console.log('NOT ALL CALCULATION LINES');
                return;
            }

            // Prevent default
            event.preventDefault();

            this.createLineService.createAndInsertCalculationLines(JSON.parse(data), true, 'paste');

        } else if (isCalculationTailApi(selected)) {

            // Check if data contains only calculation lines
            if (!parsedLines.every(isCalculationTailApi)) {
                console.log('NOT ALL TAIL LINES');
                return;
            }

            // Prevent default
            event.preventDefault();

            this.createLineService.createAndInsertTailLines(JSON.parse(data), true, 'paste');
        }
    }
}
